import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext({});

const ADMIN_EMAIL = 'firdausleo@kunlene-sz.com';

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null); // {role: 'admin'|'data_updater'|'viewer'}
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (currentUser) => {
    if (!currentUser) {
      setProfile(null);
      return;
    }
    
    // Bootstrap admin: if it's the admin email, ensure their profile exists with admin role
    const isAdminEmail = currentUser.email === ADMIN_EMAIL;
    
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', currentUser.id)
        .maybeSingle();

      if (error) {
        console.warn('Profile load error (table may not exist yet):', error.message);
        // Fallback: derive role from email for the admin
        setProfile({ role: isAdminEmail ? 'admin' : 'viewer', email: currentUser.email });
        return;
      }

      if (!data && isAdminEmail) {
        // Self-bootstrap admin profile on first login
        const { data: created } = await supabase
          .from('user_profiles')
          .insert({
            user_id: currentUser.id,
            email: currentUser.email,
            role: 'admin',
            invited_by: 'system',
          })
          .select()
          .single();
        setProfile(created || { role: 'admin', email: currentUser.email });
      } else if (!data) {
        // Non-admin without profile = no access yet (admin must invite)
        setProfile({ role: 'viewer', email: currentUser.email, no_profile: true });
      } else {
        setProfile(data);
      }
    } catch (e) {
      console.error('Profile load failed:', e);
      setProfile({ role: isAdminEmail ? 'admin' : 'viewer', email: currentUser.email });
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) loadProfile(session.user).finally(() => setLoading(false));
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) loadProfile(session.user).finally(() => setLoading(false));
      else { setProfile(null); setLoading(false); }
    });

    return () => subscription.unsubscribe();
  }, [loadProfile]);

  async function signOut() {
    await supabase.auth.signOut();
    window.location.reload();
  }

  const role = profile?.role || 'viewer';
  
  const value = {
    session,
    user,
    profile,
    loading,
    signOut,
    role,
    needsPasswordSetup: profile?.password_set === false && !profile?.no_profile,
    isAdmin:       role === 'admin',
    isDataUpdater: role === 'admin' || role === 'data_updater',
    isViewer:      true,  // everyone authenticated can view
    canEdit:       role === 'admin' || role === 'data_updater',
    canSave:       role === 'admin' || role === 'data_updater',
    canManageUsers: role === 'admin',
    refreshProfile: () => loadProfile(user),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
